import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMenuData, DbProduct } from '@/hooks/useMenuData';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import RestaurantLogo from '@/components/branding/RestaurantLogo';
import HalalBadge from '@/components/branding/HalalBadge';
import { useBranding } from '@/hooks/useBranding';
import JustEatButton from '@/components/feature/JustEatButton';
import CategoryNav from './components/CategoryNav';
import MenuSection from './components/MenuSection';
import MenuProductPanel from './components/MenuProductPanel';
import { restaurantInfo } from '@/mocks/restaurantData';
import { usePageMeta } from '@/hooks/usePageMeta';
import { SEO } from '@/content/yawasCopy';
import {
  DEFAULT_RESTAURANT_HOURS,
  DEFAULT_RESTAURANT_HOURS_SHORT,
  isRestaurantOpenNow,
} from '@/content/openingHours';

const MenuPage = () => {
  usePageMeta(SEO.menu);

  const { categories, products, loading, error } = useMenuData();
  const { get: getSetting } = useSiteSettings();
  const { logoPlacement, logoSize } = useBranding();
  const menuHeaderHeight = Math.max(64, Math.min(Math.round(logoSize * 0.85 + 24), 96));
  const menuLogoHeight = menuHeaderHeight - 16;

  const hoursRaw = getSetting('restaurant_hours', DEFAULT_RESTAURANT_HOURS);
  const restaurantHours = hoursRaw.includes('\n')
    ? DEFAULT_RESTAURANT_HOURS_SHORT
    : hoursRaw || DEFAULT_RESTAURANT_HOURS_SHORT;
  const isOpen = isRestaurantOpenNow();

  const [activeCategory, setActiveCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState<DbProduct | null>(null);
  const [search, setSearch] = useState('');
  const [mobileView, setMobileView] = useState<'menu' | 'detail'>('menu');
  const [pendingScrollCategory, setPendingScrollCategory] = useState<string | null>(null);

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);

  const itemCounts = categories.reduce((acc, cat) => {
    acc[cat.id] = products.filter((p) => p.category_id === cat.id).length;
    return acc;
  }, {} as Record<string, number>);

  const filteredItems = (catId: string) => {
    const items = products.filter((p) => p.category_id === catId);
    if (!search) return items;
    return items.filter(
      (p) =>
        p.name_de.toLowerCase().includes(search.toLowerCase()) ||
        p.description_de.toLowerCase().includes(search.toLowerCase())
    );
  };

  const allSearchResults = search
    ? products.filter(
        (p) =>
          p.name_de.toLowerCase().includes(search.toLowerCase()) ||
          p.description_de.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const scrollToCategory = useCallback((catId: string) => {
    setActiveCategory(catId);
    setPendingScrollCategory(catId);
    setMobileView('menu');
  }, []);

  useEffect(() => {
    if (mobileView !== 'menu' || !pendingScrollCategory) return;
    const catId = pendingScrollCategory;
    const timeoutId = window.setTimeout(() => {
      const el = sectionRefs.current[catId];
      const container = scrollContainerRef.current;
      if (el && container) {
        container.scrollTo({ top: Math.max(0, el.offsetTop - 80), behavior: 'smooth' });
      }
      setPendingScrollCategory(null);
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [mobileView, pendingScrollCategory]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || categories.length === 0) return;
    const handleScroll = () => {
      const scrollTop = container.scrollTop + 100;
      let current = categories[0].id;
      for (const cat of categories) {
        const el = sectionRefs.current[cat.id];
        if (el && el.offsetTop <= scrollTop) current = cat.id;
      }
      setActiveCategory(current);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [categories]);

  const handleSelectItem = (item: DbProduct) => {
    setSelectedItem(item);
    setMobileView('detail');
  };

  return (
    <div className="h-dvh flex flex-col bg-stone-50 overflow-hidden">
      <header className="relative z-50 flex-shrink-0 bg-white border-b border-stone-100">
        <div
          className={`relative flex items-center gap-4 pl-4 md:pl-6 pr-14 md:pr-16 ${logoPlacement === 'center' ? 'justify-center' : ''}`}
          style={{ height: menuHeaderHeight }}
        >
          <Link to="/" className={`flex items-center gap-2.5 flex-shrink-0 ${logoPlacement === 'center' ? 'absolute left-4 md:left-6' : ''}`}>
            <RestaurantLogo maxHeight={menuLogoHeight} />
          </Link>

          <div className="w-px h-6 bg-stone-200 flex-shrink-0 hidden sm:block" />

          <div className="flex-1 max-w-md relative">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Gericht suchen..."
              className="w-full pl-9 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-full text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-stone-400 hover:text-stone-600"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-full ${isOpen ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`}></div>
              <span className={`text-xs font-medium ${isOpen ? 'text-green-700' : 'text-red-600'}`}>
                {isOpen ? 'Geöffnet' : 'Geschlossen'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-stone-500 text-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-stone-400 text-sm"></i>
              </div>
              <span>{restaurantHours}</span>
            </div>
          </div>

          <JustEatButton className="hidden sm:flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ml-auto">
            <i className="ri-external-link-line text-sm"></i>
            Just Eat
          </JustEatButton>

          <HalalBadge
            size={Math.max(36, Math.min(48, Math.round(menuLogoHeight * 0.62)))}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2"
          />
        </div>

        {!loading && categories.length > 0 && (
          <div className="lg:hidden relative z-50 overflow-x-auto border-t border-stone-100 bg-white pointer-events-auto">
            <div className="flex items-center gap-1 px-4 py-2.5 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onPointerDown={() => scrollToCategory(cat.id)}
                  onClick={() => scrollToCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer touch-manipulation ${
                    activeCategory === cat.id
                      ? 'bg-amber-500 text-white'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name_de}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <div className="relative z-0 flex flex-1 min-h-0 overflow-hidden">
        {!loading && categories.length > 0 && (
          <aside className="hidden lg:flex flex-col w-56 xl:w-64 flex-shrink-0 bg-white border-r border-stone-100 overflow-y-auto">
            <div className="px-3 py-5">
              <p className="text-stone-400 text-xs font-semibold uppercase tracking-widest px-4 mb-3">
                Kategorien
              </p>
              <CategoryNav
                categories={categories}
                activeCategory={activeCategory}
                onSelect={scrollToCategory}
                itemCounts={itemCounts}
              />
            </div>
          </aside>
        )}

        <div
          ref={scrollContainerRef}
          className={`flex-1 overflow-y-auto px-4 md:px-6 py-6 min-w-0 ${mobileView === 'detail' ? 'hidden lg:block' : ''}`}
        >
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
              <div className="w-10 h-10 border-[3px] border-amber-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-stone-400 text-sm">Speisekarte wird geladen...</p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-2xl">
                <i className="ri-wifi-off-line text-2xl text-red-400"></i>
              </div>
              <p className="text-stone-600 font-medium text-sm">Menü konnte nicht geladen werden</p>
              <p className="text-stone-400 text-xs">{error}</p>
            </div>
          )}

          {!loading && !error && search && (
            <div>
              <p className="text-stone-500 text-sm mb-4">
                {allSearchResults.length} Ergebnis{allSearchResults.length !== 1 ? 'se' : ''} für &ldquo;{search}&rdquo;
              </p>
              {allSearchResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-2xl mb-4">
                    <i className="ri-search-line text-2xl text-stone-300"></i>
                  </div>
                  <p className="text-stone-500 font-medium text-sm">Keine Gerichte gefunden</p>
                </div>
              ) : (
                <MenuSection
                  category={{ id: 'search', name_de: 'Suchergebnisse', name_tr: '', icon: 'ri-search-line', sort_order: 0, is_active: true }}
                  items={allSearchResults}
                  onSelectItem={handleSelectItem}
                />
              )}
            </div>
          )}

          {!loading && !error && !search && categories.map((cat) => (
            <div
              key={cat.id}
              ref={(el) => { sectionRefs.current[cat.id] = el; }}
            >
              <MenuSection
                category={cat}
                items={filteredItems(cat.id)}
                onSelectItem={handleSelectItem}
              />
            </div>
          ))}

          {!loading && !error && categories.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <p className="text-stone-500 text-sm">Noch keine Speisekarte vorhanden.</p>
              <p className="text-stone-400 text-xs mt-1">{restaurantInfo.name}</p>
            </div>
          )}
        </div>

        <aside className={`w-full lg:w-[380px] xl:w-[420px] min-h-0 flex-shrink-0 bg-white border-l border-stone-100 overflow-y-auto overscroll-contain ${mobileView === 'menu' ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'}`}>
          <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-stone-100">
            <button
              onClick={() => setMobileView('menu')}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-stone-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <span className="text-sm font-semibold text-stone-800">Details</span>
          </div>
          <MenuProductPanel item={selectedItem} />
        </aside>
      </div>
    </div>
  );
};

export default MenuPage;
